import React, { FC, useMemo } from 'react'
import TextField from '@material-ui/core/TextField/TextField'
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress'
import SvgIcon from '@material-ui/core/SvgIcon'
import { ReactComponent as CircleArrowIcon } from 'assets/svg/CircleArrowBottomIcon.svg'
import { ReactComponent as CircleCloseIcon } from 'assets/svg/CircleCloseIcon.svg'
import Autocomplete, { AutocompleteChangeDetails, AutocompleteChangeReason } from '@material-ui/lab/Autocomplete/Autocomplete'
import { useQuery } from 'react-query'
import { Value } from '@material-ui/lab'
import { getIsoLight } from 'services/iso'
import { IIsoListItemLight } from 'typescript/interfaces/iso'
import { Box, Chip } from '@material-ui/core'
import { useSelectComponentStyle } from 'UI/Select'
import cn from 'classnames'
import { useMultipleAutocompleteStyles } from 'components/RolesAutoComplete'

interface IAutoCompleteProps {
  isoId: any
  onChange?: (
    event: React.ChangeEvent<{}>,
    value: Value<any, any, any, any>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any>,
  ) => void
  error?: boolean
  helperText?: string | false
  label?: string
  disabled?: boolean
  smoothLeft?: boolean
  smoothRight?: boolean
  multiple?: boolean
  className?: string
}

const ISOAutoComplete: FC<IAutoCompleteProps> = ({
  isoId,
  onChange,
  helperText,
  error,
  label = 'ISO',
  disabled,
  className,
  smoothLeft,
  smoothRight,
  multiple,
}) => {
  const { data, isLoading } = useQuery(['iso'], () => getIsoLight({ PageSize: 4000 }))
  const arrayCompanies = useMemo(() => {
    return data ? data.data : []
  }, [data])
  const selectedOptionClient = useMemo(() => {
    if (data) {
      const findClient = data.data.find((c) => c.id === isoId) || null
      return findClient
    }
    return null
  }, [data, isoId])

  const classes = useSelectComponentStyle()
  const mClasses = useMultipleAutocompleteStyles()

  const arraySelectedOptions = useMemo(() => {
    if (Array.isArray(isoId) && data) {
      return arrayCompanies.filter((c) => isoId.includes(c.id))
    }
    return []
  }, [arrayCompanies, isoId])

  return (
    <Autocomplete
      multiple={Boolean(multiple)}
      value={multiple ? arraySelectedOptions : selectedOptionClient}
      disabled={disabled}
      loading={isLoading}
      options={arrayCompanies}
      classes={{ inputRoot: cn(mClasses.inputRoot, multiple), root: className }}
      renderTags={(tagValue, getTagProps) => {
        return tagValue.map((option: IIsoListItemLight, index) => <Chip label={option.businessName} {...getTagProps({ index })} />)
      }}
      getOptionLabel={(option: IIsoListItemLight) => {
        return option.businessName
      }}
      getOptionSelected={(option: IIsoListItemLight, value) => {
        if (value && value.id) return option.id === value.id
        return false
      }}
      onChange={onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          helperText={helperText}
          error={error}
          label={label}
          className={cn({ [classes.disabledFormControlLeftBorder]: smoothLeft }, { [classes.disabledFormControlRightBorder]: smoothRight })}
          InputProps={{
            ...params.InputProps,
            ref: params.InputProps.ref,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                {selectedOptionClient && (
                  <Box mr="0.25rem" display="flex" alignItems="center" justifyContent="center" style={{ cursor: 'pointer' }}>
                    <SvgIcon
                      fontSize="inherit"
                      viewBox="0 0 20 20"
                      component={CircleCloseIcon}
                      onClick={(e: any) => {
                        onChange!(e, undefined, 'clear')
                      }}
                    />
                  </Box>
                )}
                <SvgIcon fontSize="inherit" viewBox="0 0 20 20" component={CircleArrowIcon} />
              </>
            ),
          }}
          variant="outlined"
        />
      )}
    />
  )
}

export default ISOAutoComplete
